import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { ContactForm, contactSchema } from "../../schemas/contactSchema";
import { Contact } from "../../models/Contact";
import { useEffect, useMemo, useState } from "react";
import { useApiProvider } from "../../providers/api/apiProvider";
import { deleteContact, saveContact, updateContact } from "../../services/contactServices";
import { IShowMessage } from "../../types/show";

import countries from "../../consts/countries.json";

export type InitialContactModalValue = Pick<Contact, "name" | "phone">;

const onlyDigits = ( value: string | null | undefined ) => ( value ?? "" ).replace(/\D/g, "" );

const resolvePhoneCountry = ( phone: string | null | undefined ) => {
  const digits = onlyDigits( phone );
  const defaultCountry = countries.find(( country ) => country.iso === "BR" ) ?? countries[ 0 ]!;
  const matchedCountry = countries.find(( country ) => digits.startsWith( onlyDigits( country.code ))) ?? defaultCountry;
  const countryCode = onlyDigits( matchedCountry?.code );
  const localPhone = countryCode && digits.startsWith( countryCode ) ? digits.slice( countryCode.length ) : digits;

  return {
    country: matchedCountry,
    localPhone,
  };
};

export const useContactModalController = ( contact: Contact | null, initialContact: InitialContactModalValue | null | undefined, open: boolean, onClose: () => void ) => {
  const {
    simpleApiCall,
  } = useApiProvider();

  const form = useForm<ContactForm>({
    resolver: yupResolver( contactSchema ),
    defaultValues: {
      id: null,
      name: '',
      phone: '',
      ddi: countries.find(( country ) => country.iso === "BR"),
    },
    mode: 'all',
  });

  const [ showMessage, setShowMessage ] = useState<Omit<IShowMessage, "lock">>({
    delete: false,
  });

  const toggleShowMessage = ( field: keyof Omit<IShowMessage, "lock"> ) => {
    setShowMessage(( value ) => {
      const toggle = !value[ field ];

      value.delete = false;

      value[ field ] = toggle;

      return { ...value };
    });
  };

  const onSubmit = async ( data: ContactForm ) => {
    const response = await simpleApiCall<ContactForm, boolean>( data.id ? updateContact : saveContact, data );

    if( response !== null ) onClose();
  };

  const handleSubmit = form.handleSubmit( onSubmit );

  const handleDelete = async () => {
    const response = await simpleApiCall<string, boolean>( deleteContact, contact?.id ?? "" );

    if( response !== null ) onClose();
  };

  useEffect(() => {
    if( !open ) {
      form.reset();

      setShowMessage({
        delete: false,
      });
    } else if( contact !== null ) {
      const phone = resolvePhoneCountry( contact.phone );

      form.setValue("id", contact.id );
      form.setValue("name", contact.name );
      form.setValue("ddi", phone.country );
      form.setValue("phone", phone.localPhone, {
        shouldValidate: true,
      });
    } else if( initialContact ) {
      const phone = resolvePhoneCountry( initialContact.phone );

      form.setValue("id", null );
      form.setValue("name", initialContact.name, {
        shouldValidate: true,
      });
      form.setValue("ddi", phone.country );
      form.setValue("phone", phone.localPhone, {
        shouldValidate: true,
      });
    }
    // eslint-disable-next-line
  }, [ open ]);

  return useMemo(() => ({
    form,
    showMessage,
    countries,
    handleSubmit,
    toggleShowMessage,
    handleDelete,
    // eslint-disable-next-line
  }), [
    form,
    showMessage,
  ]);
};
